import type {PickingInfo, UpdateParameters} from '@deck.gl/core';
import {CompositeLayer} from '@deck.gl/core';
import {IconLayer, IconLayerProps} from '@deck.gl/layers';
import type {ClusterFeature, ClusterProperties, PointFeature} from 'supercluster';
import Supercluster from 'supercluster';
import {IconUtil} from "../../util/IconUtil";
import {IconInfo} from "../../util/IconInfo";
import {DatasetRow} from "../../data/Dataset";
import {LayerDef} from "../../types";
import {ColorUtils} from "../../util/ColorUtils";
import {Color, ColorPalette} from "../../util/Color";
import {LayerUtil} from "../LayerUtil";


const CLUSTER_LIMIT = 1000;

export type IconClusterLayerPickingInfo<DatasetRow> = PickingInfo<
    DatasetRow | (DatasetRow & ClusterProperties),
    { objects?: DatasetRow[] }
>;

export default class IconClusterLayer<ExtraProps extends {} = {}>
    extends CompositeLayer<Required<IconLayerProps<DatasetRow>> & ExtraProps> {
    state!: {
        data: (PointFeature<DatasetRow> | ClusterFeature<DatasetRow>)[];
        index: Supercluster<DatasetRow, DatasetRow>;
        z: number;
    };

    shouldUpdateState({changeFlags}: UpdateParameters<this>) {
        return changeFlags.somethingChanged;
    }

    updateState({props, oldProps, changeFlags}: UpdateParameters<this>) {
        const rebuildIndex = changeFlags.dataChanged || props.sizeScale !== oldProps.sizeScale;

        if (rebuildIndex) {
            const index = new Supercluster<DatasetRow, DatasetRow>({
                maxZoom: 16,
                radius: props.sizeScale * Math.sqrt(2)
            });
            index.load(
                // @ts-ignore Supercluster expects proper GeoJSON feature
                (props.data as DatasetRow[]).map(row => ({
                    geometry: {coordinates: row.getLonLat()},
                    properties: row
                }))
            );
            this.setState({index});
        }

        const z = Math.floor(this.context.viewport.zoom);
        if (rebuildIndex || z !== this.state.z) {
            this.setState({
                data: this.state.index.getClusters([-180, -85, 180, 85], z),
                z
            });
        }
    }

    getPickingInfo({info, mode}: {
        info: PickingInfo<PointFeature<DatasetRow> | ClusterFeature<DatasetRow>>;
        mode: string;
    }): IconClusterLayerPickingInfo<DatasetRow> {
        const pickedObject = info.object?.properties;
        if (pickedObject) {
            let objects: DatasetRow[] | undefined;
            // if (pickedObject.cluster && mode !== 'hover') {
            if (pickedObject["cluster"]) {
                objects = this.state.index.getLeaves(pickedObject["cluster_id"], CLUSTER_LIMIT).map(f => f.properties);
            }
            return {...info, object: pickedObject, objects};
        }
        return {...info, object: undefined};
    }

    private getIcon = (info: any, layerDef: LayerDef, buckets: number[], defaultIconDataUrl: any) => {
        if (info.properties["cluster"]) {
            const dataRows = this.state.index.getLeaves(info.properties["cluster_id"], CLUSTER_LIMIT).map(f => f.properties);
            let colorPalette: ColorPalette;
            let counts: number[];

            if (layerDef.isColorFieldNumeric()) {
                colorPalette = layerDef.config.colorPalette;
                counts = new Array(colorPalette.length).fill(0);
                for (const row of dataRows) {
                    const colIndex = ColorUtils.getColorIndexForValue(
                        (row as DatasetRow).getNumberValue(layerDef.config.colorFieldId),
                        buckets
                    );
                    if (!isNaN(colIndex)) {
                        counts[colIndex]++;
                    }
                }
            } else {
                let colorMap = LayerUtil.prepareColorMap(layerDef);
                colorPalette = Array.from(Object.values(colorMap)).map(s => Color.createFromString(s));
                counts = new Array(colorPalette.length).fill(0);
                for (const row of dataRows) {
                    const rowColStr = LayerUtil.calcFillColor(layerDef, row, buckets)?.toHex();
                    let idx = colorPalette.findIndex(c => c.toHex() === rowColStr);
                    if (idx < 0) {
                        idx = 0;
                    }
                    counts[idx]++;
                }
            }

            return {
                url: IconUtil.createClusterDonutIcon(counts, colorPalette),
                width: IconUtil.MAX_ICON_SIZE,
                height: IconUtil.MAX_ICON_SIZE,
                mask: false
            }
        }
        return {
            url: defaultIconDataUrl,
            width: IconUtil.MAX_ICON_SIZE,
            height: IconUtil.MAX_ICON_SIZE,
            mask: true
        };
    }

    renderLayers() {
        const {data} = this.state;
        const {sizeScale} = this.props;
        // @ts-ignore
        const layerDef: LayerDef = this.props.layerDef;
        // @ts-ignore
        const buckets: number[] = this.props.buckets;
        const iconDataUrl = IconUtil.iconInfoToURL(IconUtil.getPointShapeById(layerDef.config.shape)?.iconInfo ?? IconInfo.CIRCLE);

        const calcIconSize = (d) => {
            let count = d.properties.cluster ? d.properties.point_count : 1;
            let size: number;
            if (count > 100) {          // 100 - 150
                size = 100;
            } else if (count > 50) {    // 90 - 100
                size = 90 + Math.trunc(count / 50 * 10);
            } else if (count > 20) {    // 80 - 90
                size = 80 + Math.trunc(count / 30 * 10);
            } else if (count > 10) {    // 70 - 80
                size = 70 + Math.trunc(count / 20 * 10);
            } else if (count >= 2) {     // 60 - 70
                size = 60 + Math.trunc(count / 10 * 10);
            } else {
                size = layerDef.config.size;
            }
            return size * (0.4 + layerDef.config.size / 100) / sizeScale;
        }

        return new IconLayer<PointFeature<DatasetRow> | ClusterFeature<DatasetRow>>(
            {
                data,
                sizeScale,
                getPosition: d => d.geometry.coordinates as [number, number],
                getIcon: info => this.getIcon(info, layerDef, buckets, iconDataUrl),
                getSize: d => calcIconSize(d),
                getColor: d => d.properties["cluster"] ? [0, 0, 0] : LayerUtil.calcFillColor(layerDef, (d.properties as DatasetRow), buckets)?.toTriple()
            },
            this.getSubLayerProps({id: "icon"})
        );
    }

}